/**
 * LojaPilot CSV catalog import — parsing, column mapping, validation and
 * conversion to Woo-shaped product payloads. Pure functions (no I/O) so the
 * whole pipeline is unit-testable.
 */

export const CSV_MAX_ROWS = 2000;
export const CSV_MAX_BYTES = 2 * 1024 * 1024; // 2 MB

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

const DELIMITERS = [';', ',', '\t', '|'] as const;

export const detectDelimiter = (headerLine: string): string => {
  let best: string = ',';
  let bestCount = -1;
  for (const d of DELIMITERS) {
    const count = headerLine.split(d).length - 1;
    if (count > bestCount) { best = d; bestCount = count; }
  }
  return bestCount > 0 ? best : ',';
};

/**
 * RFC-4180-ish parser: quoted fields, doubled-quote escapes, CRLF/LF,
 * embedded newlines inside quotes. Returns a matrix of raw strings.
 */
export const parseCsv = (input: string, delimiter?: string): string[][] => {
  const text = input.replace(/^﻿/, '');
  const firstLine = text.slice(0, text.indexOf('\n') === -1 ? text.length : text.indexOf('\n'));
  const delim = delimiter || detectDelimiter(firstLine);

  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  let started = false;

  const pushField = () => { row.push(field); field = ''; started = false; };
  const pushRow = () => {
    // Skip fully-empty trailing row
    if (row.length === 1 && row[0] === '' && rows.length > 0) { row = []; return; }
    if (row.length === 0) return;
    rows.push(row); row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"' && !started) { inQuotes = true; started = true; continue; }
    if (ch === delim) { pushField(); continue; }
    if (ch === '\r') continue;
    if (ch === '\n') { pushField(); pushRow(); continue; }
    field += ch;
    started = true;
  }
  pushField();
  pushRow();
  return rows;
};

// ---------------------------------------------------------------------------
// Injection safety
// ---------------------------------------------------------------------------

/** Neutralizes spreadsheet-formula prefixes so imported values can never be
 *  interpreted as formulas when re-exported or opened in Excel. */
export const sanitizeCell = (value: string): string => {
  const v = String(value ?? '').trim();
  if (/^[=+\-@\t\r]/.test(v)) return `'${v}`;
  return v;
};

const isSafeImageUrl = (value: string): boolean => {
  if (!value) return true;
  try {
    const u = new URL(value);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
};

// ---------------------------------------------------------------------------
// Field mapping
// ---------------------------------------------------------------------------

export interface CsvField {
  key: string;
  label: string;
  required: boolean;
  synonyms: string[];
}

export const PRODUCT_FIELDS: CsvField[] = [
  { key: 'name', label: 'Nome', required: true, synonyms: ['name', 'nome', 'title', 'titulo', 'título', 'product_name', 'produto'] },
  { key: 'sku', label: 'SKU', required: false, synonyms: ['sku', 'reference', 'referencia', 'referência', 'ref'] },
  { key: 'description', label: 'Descrição', required: false, synonyms: ['description', 'descricao', 'descrição', 'long_description'] },
  { key: 'short_description', label: 'Descrição curta', required: false, synonyms: ['short_description', 'descricao_curta', 'descrição curta', 'resumo', 'excerpt'] },
  { key: 'regular_price', label: 'Preço normal', required: true, synonyms: ['regular_price', 'price', 'preco', 'preço', 'preco_normal', 'pvp'] },
  { key: 'sale_price', label: 'Preço promocional', required: false, synonyms: ['sale_price', 'preco_promocional', 'preço_promocional', 'promo', 'preco_sale', 'sale'] },
  { key: 'stock_quantity', label: 'Stock', required: false, synonyms: ['stock_quantity', 'stock', 'quantity', 'quantidade', 'qty', 'inventario', 'inventário'] },
  { key: 'stock_status', label: 'Estado do stock', required: false, synonyms: ['stock_status', 'estado_stock', 'stockstatus', 'availability', 'disponibilidade'] },
  { key: 'categories', label: 'Categorias', required: false, synonyms: ['categories', 'categorias', 'category', 'categoria'] },
  { key: 'images', label: 'Imagens (URLs)', required: false, synonyms: ['images', 'imagens', 'image', 'imagem', 'image_url', 'photo', 'foto'] },
  { key: 'type', label: 'Tipo', required: false, synonyms: ['type', 'tipo', 'product_type'] },
  { key: 'status', label: 'Estado', required: false, synonyms: ['status', 'estado', 'published', 'publicado'] },
];

/** Maps each source column index to a product field key (or null). */
export const autoMapColumns = (headers: string[]): (string | null)[] => {
  const used = new Set<string>();
  return headers.map((h) => {
    const normalized = String(h || '').trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    for (const field of PRODUCT_FIELDS) {
      if (used.has(field.key)) continue;
      if (field.synonyms.some((s) => normalized === s.normalize('NFD').replace(/[̀-ͯ]/g, '') || normalized.includes(s.normalize('NFD').replace(/[̀-ͯ]/g, '')))) {
        used.add(field.key);
        return field.key;
      }
    }
    return null;
  });
};

export interface MappedRow {
  rowIndex: number;          // 0-based data row (excludes header)
  raw: string[];
  values: Record<string, string>;
}

export const mapRows = (rows: string[][], columnMap: (string | null)[]): MappedRow[] => {
  if (rows.length < 2) return [];
  return rows.slice(1).map((raw, i) => {
    const values: Record<string, string> = {};
    raw.forEach((cell, colIdx) => {
      const field = columnMap[colIdx];
      if (field) values[field] = sanitizeCell(cell);
    });
    return { rowIndex: i, raw, values };
  }).filter((r) => Object.values(r.values).some((v) => v !== ''));
};

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export type ImportStatus = 'valid' | 'warning' | 'error';

export interface RowValidation {
  status: ImportStatus;
  errors: string[];
  warnings: string[];
}

const PRICE_RE = /^\d{1,10}([.,]\d{1,2})?$/;
const normalizePrice = (v: string) => v.replace(/\s/g, '').replace(/[€$£]|R\$/g, '').replace(',', '.');

export const validateRow = (row: MappedRow): RowValidation => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const v = row.values;

  if (!v.name || !v.name.trim()) errors.push('Nome em falta');
  if (!v.regular_price || !v.regular_price.trim()) {
    errors.push('Preço normal em falta');
  } else if (!PRICE_RE.test(normalizePrice(v.regular_price))) {
    errors.push(`Preço inválido: "${v.regular_price}"`);
  }
  if (v.sale_price && !PRICE_RE.test(normalizePrice(v.sale_price))) {
    errors.push(`Preço promocional inválido: "${v.sale_price}"`);
  }
  if (v.stock_quantity !== undefined && v.stock_quantity !== '' && !/^-?\d{1,7}$/.test(v.stock_quantity.trim())) {
    errors.push(`Stock inválido: "${v.stock_quantity}"`);
  }
  if (v.stock_status && !['instock', 'outofstock', 'onbackorder'].includes(v.stock_status.trim().toLowerCase())) {
    warnings.push(`Estado de stock desconhecido "${v.stock_status}" — assume "instock"`);
  }
  if (v.type && !['simple', 'variable', 'grouped', 'external'].includes(v.type.trim().toLowerCase())) {
    warnings.push(`Tipo "${v.type}" não suportado — será importado como "simple"`);
  }
  if (v.status && !['publish', 'draft', 'pending', 'private'].includes(v.status.trim().toLowerCase())) {
    warnings.push(`Estado "${v.status}" inválido — será "publish"`);
  }
  if (v.images) {
    for (const url of v.images.split('|').map((s) => s.trim()).filter(Boolean)) {
      if (!isSafeImageUrl(url)) errors.push(`URL de imagem inválido: "${url}"`);
    }
  }
  return { status: errors.length ? 'error' : warnings.length ? 'warning' : 'valid', errors, warnings };
};

/** Detects duplicate SKUs and duplicate names within the file itself. */
export const findDuplicates = (rows: MappedRow[]): Map<number, string> => {
  const flags = new Map<number, string>();
  const bySku = new Map<string, number>();
  const byName = new Map<string, number>();
  for (const r of rows) {
    const sku = (r.values.sku || '').trim().toLowerCase();
    const name = (r.values.name || '').trim().toLowerCase();
    if (sku) {
      if (bySku.has(sku)) { flags.set(r.rowIndex, 'SKU duplicado no ficheiro'); flags.set(bySku.get(sku)!, 'SKU duplicado no ficheiro'); }
      else bySku.set(sku, r.rowIndex);
    }
    if (name) {
      if (byName.has(name)) flags.set(r.rowIndex, flags.get(r.rowIndex) || 'Nome duplicado no ficheiro');
      else byName.set(name, r.rowIndex);
    }
  }
  return flags;
};

// ---------------------------------------------------------------------------
// Conversion to Woo-shaped payloads
// ---------------------------------------------------------------------------

const slugify = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'produto';

export const toWooProduct = (row: MappedRow, categoryIds: Map<string, number> = new Map()): Record<string, any> => {
  const v = row.values;
  const cats = (v.categories || '').split('|').map((s) => s.trim()).filter(Boolean)
    .map((name) => categoryIds.has(name.toLowerCase()) ? { id: categoryIds.get(name.toLowerCase()) } : { name });
  const images = (v.images || '').split('|').map((s) => s.trim()).filter(Boolean).map((src) => ({ src }));
  const stockQty = v.stock_quantity !== undefined && v.stock_quantity !== '' ? parseInt(v.stock_quantity.trim(), 10) : null;
  const stockStatus = v.stock_status && ['instock', 'outofstock', 'onbackorder'].includes(v.stock_status.trim().toLowerCase())
    ? v.stock_status.trim().toLowerCase() : (stockQty !== null && stockQty <= 0 ? 'outofstock' : 'instock');
  const type = v.type && ['simple', 'variable', 'grouped', 'external'].includes(v.type.trim().toLowerCase())
    ? v.type.trim().toLowerCase() : 'simple';

  return {
    name: v.name.trim(),
    slug: slugify(v.name),
    type,
    status: v.status && ['publish', 'draft', 'pending', 'private'].includes(v.status.trim().toLowerCase()) ? v.status.trim().toLowerCase() : 'publish',
    description: v.description || '',
    short_description: v.short_description || '',
    sku: v.sku || '',
    regular_price: normalizePrice(v.regular_price),
    sale_price: v.sale_price ? normalizePrice(v.sale_price) : '',
    stock_status: stockStatus,
    stock_quantity: stockQty,
    manage_stock: stockQty !== null,
    categories: cats,
    images,
    meta_data: [{ key: '_imported_via', value: 'lojapilot_csv' }],
  };
};

export const CSV_TEMPLATE = [
  'name;sku;regular_price;sale_price;stock_quantity;stock_status;categories;images;description;short_description;type;status',
  'T-Shirt Básica;TS-001;19.99;14.99;50;instock;Vestuário|T-Shirts;https://exemplo.com/tshirt.jpg;T-shirt em algodão orgânico.;T-shirt básica;simple;publish',
  'Caneca Cerâmica;MG-002;12.50;;200;instock;Casa|Cozinha;https://exemplo.com/caneca.jpg;Caneca de cerâmica 350ml.;Caneca;simple;publish',
].join('\n');

export interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: { rowIndex: number; message: string }[];
}
